import { Component, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { ButlerService } from "../../services/api/butler/butler.service";
import { Router } from "@angular/router";
import { TableModule } from "@openng/optimus-ui/table";
import { SvgIconComponent } from "../../shared/components/svg-icon/svg-icon.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { RuleChainComponent } from "./components/forms/rule-chain/rule-chain.component";
import { YesNoPipe } from "../../shared/pipes/yesNo/yes-no.pipe";
import { ConfirmationService } from "@openng/optimus-ui/api";
import { RuleChain } from "../../models/api/ruleChain";
import { Column } from "../../models/ui/tableColumn";

@Component({
  standalone: true,
  selector: "app-butler",
  imports: [TableModule, SvgIconComponent, TranslateModule, YesNoPipe],
  templateUrl: "./butler.component.html",
  styleUrl: "./butler.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DialogService],
})
export class ButlerComponent implements OnInit {
  private butlerService = inject(ButlerService);
  private router = inject(Router);
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private ref: DynamicDialogRef | null = null;
  private confirmationService = inject(ConfirmationService);
  public ruleChains: WritableSignal<RuleChain[]> = signal([]);

  public ruleChainColumns: Column[] = [
    { field: "label", header: "butler_programming.rule_chains.table.label" },
    { field: "notes", header: "butler_programming.rule_chains.table.notes" },
    { field: "active", header: "butler_programming.rule_chains.table.active" },
  ];

  public ngOnInit() {
    this.butlerService.getRuleChains().subscribe((data) => {
      this.ruleChains.set(data);
    });
  }

  /**
   * Opens a dialog to create a new RuleChain.
   * If the save is successful, it will refresh the list of RuleChains.
   */
  public openCreateRuleChain() {
    this.ref = this.dialogService.open(RuleChainComponent, {
      header: this.translate.instant("butler_programming.rule_chains.add_title"),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: {
        ruleChain: {
          label: null,
          notes: null,
          active: false,
        },
      },
      closable: true,
    });
    this.ref?.onClose.subscribe((success: boolean) => {
      if (success) {
        this.butlerService.getRuleChains().subscribe((data) => {
          this.ruleChains.set(data);
        });
      }
    });
  }
  /**
   * Opens a dialog to edit a RuleChain.
   * If the save is successful, it will refresh the list of RuleChains.
   * @param ruleChain The RuleChain to edit.
   */
  public openEditRuleChain(ruleChain: RuleChain) {
    this.ref = this.dialogService.open(RuleChainComponent, {
      header: this.translate.instant("butler_programming.rule_chains.edit_title"),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: {
        ruleChain,
      },
      closable: true,
    });
    this.ref?.onClose.subscribe((success: boolean) => {
      if (success) {
        this.butlerService.getRuleChains().subscribe((data) => {
          this.ruleChains.set(data);
        });
      }
    });
  }

  /**
   * Deletes a RuleChain after confirming with the user.
   * @param event The event that triggered the deletion.
   * @param ruleChainId The id of the RuleChain to delete.
   */
  public deleteRuleChain(event: Event, ruleChainId: string) {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("butler_programming.rule_chains.delete_confirm"),
      key: "global",
      accept: () => {
        this.butlerService.deleteRuleChain(ruleChainId).subscribe({
          next: () => {
            this.butlerService.getRuleChains().subscribe({
              next: (data) => {
                this.ruleChains.set(data);
              },
            });
          },
        });
      },
    });
  }

  /**
   * Navigates to the rule chain details page for the given rule chain id.
   * @param ruleChainId The id of the rule chain to navigate to.
   */
  public openRuleChain(ruleChainId: string) {
    this.router.navigate(["dashboard/butler/ruleChainDetails", ruleChainId]);
  }
}
